import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function PublicLogin() {
  const { login } = useCustomerAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login({ tenant_slug: "abrah", ...form });
      toast.success("Welcome back!");
      const next = loc.state?.next || "/site/account";
      nav(next, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && /verify/i.test(detail || "")) {
        try { sessionStorage.setItem("aeoibcms_pending_verify_email", form.email); } catch { /* ignore */ }
        toast.info("Please verify your email — a new code was just sent.");
        nav("/site/verify", { state: { email: form.email, next: loc.state?.next || "/site/account" } });
        return;
      }
      toast.error(detail || "Invalid email or password");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
          <LogIn size={12} /> Customer sign-in
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-4 text-gray-900">
          Welcome back
        </h1>
        <p className="text-sm text-gray-600 mt-2">
          Sign in to see your orders, applications and saved details.
        </p>
      </div>
      <Card>
        <CardContent className="pt-6">
          <GoogleSignInButton intent="customer" label="Continue with Google" />
          <div className="relative flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-[10px] uppercase tracking-widest text-gray-400">or sign in with email</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                required type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="customer-login-email"
              />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input
                required type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="customer-login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="customer-login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-3 text-right">
            <Link to="/site/forgot-password" data-testid="customer-forgot-password-link"
              className="text-xs text-emerald-800 hover:underline">Forgot password?</Link>
          </div>
          <div className="mt-5 pt-5 border-t border-gray-100 text-sm text-center text-gray-600">
            New to Abrah?{" "}
            <Link
              to="/site/register"
              state={loc.state}
              className="text-emerald-800 font-semibold hover:underline"
              data-testid="customer-login-signup-link">
              Create an account
            </Link>
          </div>
          <div className="mt-3 text-center">
            <Link to="/login" className="text-[11px] text-gray-400 hover:text-gray-600 uppercase tracking-widest">
              Staff? Sign in here →
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
